// src/components/panel/TopProductsToday.js
import React, { useState, useEffect } from 'react';
import { RubyOutlined } from '@ant-design/icons';
import { Spin, Table } from 'antd';
import { analyticsService } from '../../services/analyticsService';
import { useAuth } from '../../context/AuthContext';

function TopProductsToday() {
    const { interceptorsReady } = useAuth();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState('day');

    useEffect(() => {
        if (!interceptorsReady) return;
        
        // Small delay to ensure interceptors are fully attached
        const timer = setTimeout(() => {
            loadData();
        }, 100);
        
        return () => clearTimeout(timer);
    }, [period, interceptorsReady]);

    const loadData = async () => {
        try {
            setLoading(true);
            const response = await analyticsService.getTopProducts(period, 5);
            setData(response.data || []);
            setError(null);
        } catch (err) {
            console.error('Error loading top products:', err);
            setError('Error al cargar productos destacados');
        } finally {
            setLoading(false);
        }
    };

    const getPeriodLabel = () => {
        switch (period) {
            case 'day':
                return 'HOY';
            case 'week':
                return 'SEMANA';
            case 'month':
                return 'MES';
            default:
                return 'HOY';
        }
    };

    const columns = [
        {
            title: '#',
            key: 'rank',
            width: 40,
            render: (_, __, index) => <strong>{index + 1}</strong>
        },
        {
            title: 'Producto',
            dataIndex: 'ProductName',
            key: 'ProductName',
            ellipsis: true,
            render: (text, record) => {
                // Remove "-test_de_servicio" suffix if present
                const cleanName = text ? text.replace(/-test_de_servicio$/i, '') : text;
                return (
                    <div>
                        <div style={{ fontWeight: 500 }}>{cleanName}</div>
                        <div style={{ fontSize: '0.85em', color: '#666' }}>
                            {record.Brand} - {record.product_code}
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'Cant.',
            dataIndex: 'TotalQuantity',
            key: 'quantity',
            width: 70,
            align: 'right',
            render: (val) => parseFloat(val || 0).toFixed(0)
        },
        {
            title: 'Ventas',
            dataIndex: 'TotalRevenue',
            key: 'revenue',
            width: 120,
            align: 'right',
            render: (val) => `$${parseFloat(val || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
        }
    ];

    if (loading) {
        return (
            <div className='card'>
                <div className='card-inner'>
                    <RubyOutlined className='card_icon' style={{ fontSize: '38px' }} />
                    <h2>TOP PRODUCTOS {getPeriodLabel()}</h2>
                </div>
                
                <div className='card-content-center'>
                    <Spin tip="Cargando productos..." />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className='card'>
                <div className='card-inner'>
                    <RubyOutlined className='card_icon' style={{ fontSize: '38px' }} />
                    <h2>TOP PRODUCTOS {getPeriodLabel()}</h2>
                </div>
                
                <div className='card-content-center' style={{ color: '#ff4d4f' }}>
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className='card'>
            <div className='card-inner'>
                <RubyOutlined className='card_icon' style={{ fontSize: '38px' }} />
                <h2>TOP PRODUCTOS {getPeriodLabel()}</h2>
            </div>
            
            {/* Period selector buttons */}
            <div className='category-period-selector' style={{ marginTop: '8px', marginBottom: '8px' }}>
                <button 
                    className={`category-period-btn ${period === 'day' ? 'active' : ''}`}
                    onClick={() => setPeriod('day')}
                >
                    Hoy
                </button>
                <button 
                    className={`category-period-btn ${period === 'week' ? 'active' : ''}`}
                    onClick={() => setPeriod('week')}
                >
                    Semana
                </button>
                <button 
                    className={`category-period-btn ${period === 'month' ? 'active' : ''}`}
                    onClick={() => setPeriod('month')}
                >
                    Mes
                </button>
            </div>
            
            <div className='dashboard-table-container'>
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="product_code"
                    pagination={false}
                    size="small"
                    locale={{ emptyText: 'No hay ventas en este período' }}
                />
            </div>
        </div>
    );
}

export default TopProductsToday;
